import React, {useEffect, useState} from 'react';
import {isBrowserPreviewableImage, mediaKind} from './MediaCard';

export default function MediaDetailsPanel({
    detail,
    loading,
    error,
    selectable,
    attached = false,
    onClose,
    onDelete,
    onRotate,
    onSave,
    onSelect,
    onCrop,
}) {
    const file = detail?.file;
    const [values, setValues] = useState({
        title: '',
        description: '',
        altText: '',
    });
    const [saving, setSaving] = useState(false);
    const [actionError, setActionError] = useState('');

    useEffect(() => {
        setValues({
            title: file?.title || '',
            description: file?.description || '',
            altText: file?.metadata?.altText || '',
        });
        setActionError('');
    }, [file?.uuid]);

    if (loading) {
        return (
            <div className="media-system-details" role="complementary" aria-label="Media details">
                <p className="media-system-status">Loading media details...</p>
            </div>
        );
    }
    if (error) {
        return (
            <div className="media-system-details" role="complementary" aria-label="Media details">
                <button type="button" className="media-system-close" onClick={onClose}>
                    <span aria-hidden="true">&times;</span><span className="sr-only">Close</span>
                </button>
                <p className="flash error">{error}</p>
            </div>
        );
    }
    if (!file) {
        return null;
    }

    const kind = mediaKind(file);
    const previewUrl = `${file.cdnUrl}?etag=${file.etag || file.uuid}`;

    const runAction = async action => {
        setSaving(true);
        setActionError('');
        try {
            await action();
        } catch (actionFailure) {
            setActionError(actionFailure.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div
            className="media-system-details"
            role="complementary"
            aria-label={`Edit ${file.filename}`}
        >
            {onCrop && ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'].includes(file.mimetype)
                && !file.isHidden && !file.isEncrypted && file.accessPolicy !== 'private' && (
                <button type="button" className="button" onClick={() => onCrop(file)}>
                    {file.source === 'mediaCrop' ? 'Reopen crop from original' : 'Crop a new copy'}
                </button>
            )}
            <div className="media-system-details-header">
                <div>
                    <span className="media-system-eyebrow">Media details</span>
                    <h3>{file.title || file.filename}</h3>
                </div>
                <button type="button" className="media-system-close" onClick={onClose}>
                    <span aria-hidden="true">&times;</span><span className="sr-only">Close</span>
                </button>
            </div>

            <div className="media-system-detail-preview">
                {kind === 'image' && isBrowserPreviewableImage(file) && (
                    <img src={previewUrl} alt={values.altText || file.filename} />
                )}
                {kind === 'video' && <video src={previewUrl} controls />}
                {kind === 'audio' && <audio src={previewUrl} controls />}
                {(
                    !['image', 'video', 'audio'].includes(kind)
                    || (kind === 'image' && !isBrowserPreviewableImage(file))
                ) && (
                    <a href={previewUrl} target="_blank" rel="noreferrer" className="button">
                        Open {kind === 'pdf' ? 'PDF' : kind === 'image' ? 'image file' : 'file'}
                    </a>
                )}
            </div>

            <form
                onSubmit={event => {
                    event.preventDefault();
                    runAction(() => onSave(values));
                }}
            >
                <div className="group">
                    <label htmlFor={`media-title-${file.uuid}`}>Display title</label>
                    <input
                        id={`media-title-${file.uuid}`}
                        maxLength="120"
                        value={values.title}
                        placeholder={file.filename}
                        onChange={event => setValues({...values, title: event.target.value})}
                    />
                </div>
                <div className="group">
                    <label htmlFor={`media-description-${file.uuid}`}>Description</label>
                    <textarea
                        id={`media-description-${file.uuid}`}
                        maxLength="1000"
                        rows="3"
                        value={values.description}
                        onChange={event => setValues({...values, description: event.target.value})}
                    />
                </div>
                {kind === 'image' && (
                    <div className="group">
                        <label htmlFor={`media-alt-${file.uuid}`}>Alt text</label>
                        <input
                            id={`media-alt-${file.uuid}`}
                            maxLength="255"
                            value={values.altText}
                            onChange={event => setValues({...values, altText: event.target.value})}
                        />
                        <p className="help">
                            Describe the image for accessibility and search engines.
                        </p>
                    </div>
                )}
                <div className="media-system-detail-actions">
                    <button type="submit" className="button" disabled={saving}>
                        {saving ? 'Saving...' : 'Save details'}
                    </button>
                    {kind === 'image' && (
                        <button
                            type="button"
                            className="button secondary"
                            disabled={saving}
                            onClick={() => runAction(onRotate)}
                        >
                            <i className="fas fa-redo" aria-hidden="true"></i> Rotate 90°
                        </button>
                    )}
                </div>
            </form>

            <div className="media-system-usage">
                <h4>Used in {detail.attachmentCount || 0} places</h4>
                {detail.attachments?.length ? (
                    <ul>
                        {detail.attachments.map((attachment, index) => (
                            <li key={`${attachment.type}-${attachment.uuid}-${index}`}>
                                <strong>{attachment.label}:</strong> {attachment.name}
                            </li>
                        ))}
                    </ul>
                ) : (
                    <p className="help">No product, page, or post attachments were found.</p>
                )}
            </div>

            {actionError && <p className="flash error" role="alert">{actionError}</p>}

            <div className="media-system-details-footer">
                {selectable && (
                    <button type="button" className="button" onClick={() => onSelect(file)}>
                        {attached ? 'Remove this file' : 'Select this file'}
                    </button>
                )}
                <button
                    type="button"
                    className="button danger"
                    disabled={saving || detail.attachmentCount > 0}
                    title={
                        detail.attachmentCount > 0
                            ? 'Remove this file from its attachments first'
                            : ''
                    }
                    onClick={() => {
                        const confirmed = window.confirm(
                            'Delete this file permanently? This action cannot be undone.',
                        );
                        if (confirmed) {
                            runAction(onDelete);
                        }
                    }}
                >
                    Delete file
                </button>
            </div>
        </div>
    );
}
